import React, { useCallback, useEffect, useMemo } from 'react'

import PropTypes from 'prop-types'

import { List } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { FieldArray, useFormikContext } from 'formik'

import { useAllowed } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import { idPropType } from '~/src/Lib/PropTypes'
import {
  durationToMillis,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  noop,
  shortDurationHumanizer,
} from '~/src/Lib/Utils'
import useCanEditTask from '~/src/Task/TaskManager/hooks/useCanEditTask'

import { getFieldName } from '../Form/utils'
import AssigneeItem from './AssigneeItem'

const displayName = 'Task/AssigneeList'

const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  root: {
    '& li:not(:first-child)': {
      borderTop: `1px solid ${theme.palette.appPrimary.focused}`,
    },
  }
}))

export const AssigneesListComponent = ({
  assignees = EMPTY_ARRAY,
  dialogRouteInfo = EMPTY_OBJECT,
  disabled = false,
  isReviewingSchedule = false,
  phaseId = null,
  setSpentTime = noop,
  iAmLead,
  namePrefix = null,
}) => {
  const classes = useStyles()
  const [changeTaskPermission] = useAllowed({ actions: ['change_task'] })
  const { values, setFieldValue } = useFormikContext()
  const lead = values.assigned
  const canEditTask = useCanEditTask(phaseId)

  const leadIsInAssignees = useMemo(() => assignees.some(assignee => assignee.membership === lead), [assignees, lead])
  const hasPermission = Boolean(changeTaskPermission || iAmLead())
  const timeSpent = time => shortDurationHumanizer(durationToMillis(time))

  // Set the lead value here
  const setLead = useCallback(membershipId => setFieldValue('assigned', membershipId, true), [setFieldValue])
  const removeAssignee = useCallback(currentAssignee => {
    const newAssignees = assignees.filter(assignee => assignee.membership !== currentAssignee.membership)
    setFieldValue('assignees', newAssignees, true)
  }, [assignees, setFieldValue])

  // Handles setting the lead
  useEffect(() => {
    if (!setLead) {
      console.error('setLead is not defined')
    }
    if ((!assignees.length && !lead)) return
    if (!assignees.length && lead) {
      setLead(null)
      return
    }
    logger.debug('[useEffect#setLead]', { assignees, lead, leadIsInAssignees })
    if ((assignees.length && !lead) || (assignees.length && lead && !leadIsInAssignees)) {
      setLead(assignees[0].membership)
    }
  }, [assignees, lead, leadIsInAssignees, setLead])

  logger.debug('[lead]:', lead)

  return (
    <FieldArray
      key={`${lead ?? 'no-lead'}__${disabled ? 'disabled' : 'enabled'}`}
      name={getFieldName('assignees', namePrefix)}
    >
      {arrayHelpers => (
        <List dense disablePadding classes={{ root: classes.root }} aria-label="Assignees list">
          {assignees.map((assignee, index) => (
            <AssigneeItem
              key={assignee.membership}
              arrayHelpers={arrayHelpers}
              assignee={assignee}
              dialogRouteInfo={dialogRouteInfo}
              disabled={phaseId ? !canEditTask : disabled}
              hasPermission={hasPermission}
              iAmLead={iAmLead}
              index={index}
              isReviewingSchedule={isReviewingSchedule}
              lead={lead}
              removeAssignee={removeAssignee}
              setLead={setLead}
              setSpentTime={setSpentTime}
              timeSpent={timeSpent}
            />
          ))}
        </List>
      )}
    </FieldArray>
  )
}

AssigneesListComponent.displayName = displayName

AssigneesListComponent.propTypes = {
  assignees: PropTypes.arrayOf(PropTypes.shape({
    id: idPropType,
    lastStartTs: PropTypes.string,
    membership: idPropType,
    note: idPropType,
    timeSpent: PropTypes.string
  })),
  dialogRouteInfo: PropTypes.shape({
    url: PropTypes.string
  }),
  disabled: PropTypes.bool,
  phaseId: PropTypes.number,
  isReviewingSchedule: PropTypes.bool,
  iAmLead: PropTypes.func.isRequired,
  namePrefix: PropTypes.string,
  setSpentTime: PropTypes.func,
}

const Memoized = React.memo(AssigneesListComponent)
export default Memoized
